import { HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { concatMap, delay, map } from "rxjs/operators";
import { HttpService } from "src/app/core/services/http.service";
import { handleFileDownload } from "src/app/core/utils/file";
import { isReportFailed } from "src/app/core/utils/report";
import { REPORT_CODE } from "../../../models/constants";
import { AsyncReport, AsyncReportStatus } from "../models/async-report";
import { getAsyncReport } from "../serializers/async-report-serializer";
import { AsyncReportService } from "./async-report.service";

@Injectable({
  providedIn: "root",
})
export class AsyncReportApiService {
  constructor(
    private asyncReportService: AsyncReportService,
    private httpService: HttpService,
  ) {}

  getPendingReportStatus(code: REPORT_CODE): Observable<AsyncReport | null> {
    const report: AsyncReport | null =
      this.asyncReportService.getPendingReportByCodeFromStorage(code);
    if (report) {
      const request = this.httpService.get(report.urls?.status);
      const statusToStop = [
        AsyncReportStatus.GENERATED,
        AsyncReportStatus.FAILED,
      ];
      return request.pipe(
        delay(2000),
        map((response) => {
          const report = getAsyncReport(response);
          this.asyncReportService.savePendingReportInStorage(report);
          return report;
        }),
        concatMap((response) => {
          if (!statusToStop.includes(response.status)) {
            return this.getPendingReportStatus(code);
          } else {
            if (isReportFailed(response.status)) {
              this.asyncReportService.removePendingReportFromStorage(code);
            }
            return of(response);
          }
        }),
      );
    }
    return of(null);
  }

  /**
   * Get expiring points list
   * @param request
   * @returns
   */
  startEtlProcess<T>(
    reportName: string,
    request: T,
  ): Observable<AsyncReport | null> {
    return this.httpService
      .post(`reports/${reportName}/etl-processes`, request)
      .pipe(
        map((response) => {
          if (response && response.data) {
            const report = getAsyncReport(response);
            this.asyncReportService.savePendingReportInStorage(report);
            return report;
          }
          return null;
        }),
      );
  }

  downloadCSVFile(endpoint: any) {
    return this.httpService
      .get(endpoint, null, null, {
        responseType: "blob",
        observe: "response",
      })
      .pipe(
        map((response: HttpResponse<Blob>) => {
          handleFileDownload(response);
        }),
      );
  }
}
